import React, { useEffect, useState } from 'react';
import './AllFormsMetadata.css';
import { FormMetadata, FormTableRow, FormTemplate } from '../../types';
import { useNavigate } from 'react-router-dom';

import { FaEdit, FaTrashAlt, FaTable, FaLink, FaCopy } from 'react-icons/fa';
import ColumnFilter from './ColumnFilter';
import { Languages, useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../consts/translations';
import {
  deleteFormTemplate,
  generatePreviewLink,
  getFormsMetadata,
  getFormStructure,
  getFormStrings,
  getFormTemplate,
} from '../../api/AuthorizationApi';
import { CREATE_FORM_PATH, EDIT_FORM_PATH } from '../../consts/consts';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import QuestionTableModal from './QuestionTableModal';
import {
  buildFormLanguagesById,
  defaultPreviewLanguage,
  langCodeForLanguage,
  languageFromCode,
  normalizeFormLanguageCodes,
  previewLanguageOptionsForForm,
  toFormTableRow,
} from '../../utils/languageMapping';
import { logClientError } from '../../utils/logClientError';
import {
  loadSplitTemplateArtifacts,
  mergedFormTemplateFromLoadResult,
} from '../../utils/templateLoader';

/** Preview link expiry options (hours) with Hebrew labels. Must match backend ALLOWED_EXPIRY_HOURS. */
export const PREVIEW_EXPIRY_OPTIONS: { label: string; hours: number }[] = [
  { label: 'יום אחד', hours: 24 },
  { label: 'יומיים', hours: 48 },
  { label: '3 ימים', hours: 72 },
  { label: '5 ימים', hours: 120 },
  { label: 'שבוע', hours: 168 },
  { label: 'שבועיים', hours: 336 },
];

interface AllFormsMetadataProps {
  formsMetadata: FormMetadata[];
  logout: () => void;
  setFormTemplate: (template: FormTemplate) => void;
}

const AllFormsMetadata: React.FC<AllFormsMetadataProps> = ({
  formsMetadata,
  logout,
  setFormTemplate,
}) => {
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const [filteredData, setFilteredData] = useState<FormTableRow[]>(() =>
    formsMetadata.map(toFormTableRow),
  );
  const [formLanguagesById, setFormLanguagesById] = useState<Record<string, string[]>>(() =>
    buildFormLanguagesById(formsMetadata),
  );
  const [filterValues, setFilterValues] = useState<{ [key: string]: string[] }>({});
  const [formIdToDelete, setFormIdToDelete] = useState<string | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedFormTemplate, setSelectedFormTemplate] = useState<FormTemplate | null>(null);
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const [previewLanguage, setPreviewLanguage] = useState<Languages>(Languages.HEBREW);
  const [previewExpiryHours, setPreviewExpiryHours] = useState<number | null>(null);
  const [previewLinkUrl, setPreviewLinkUrl] = useState<string | null>(null);
  const [previewLinkExpiresAt, setPreviewLinkExpiresAt] = useState<string | null>(null);
  const [previewLinkLanguage, setPreviewLinkLanguage] = useState<string | null>(null);
  const [previewLinkLoading, setPreviewLinkLoading] = useState(false);
  const [previewLinkError, setPreviewLinkError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const navigate = useNavigate();

  const previewLanguageOptions = previewLanguageOptionsForForm(
    previewFormId !== null ? formLanguagesById[previewFormId] : undefined,
  );

  useEffect(() => {
    setFormLanguagesById(buildFormLanguagesById(formsMetadata));
    setFilteredData(formsMetadata.map(toFormTableRow));
  }, [formsMetadata]);

  const columns = [
    { key: 'form_id', label: 'מזהה הטופס' },
    { key: 'form_name', label: 'שם הטופס' },
    { key: 'created_by', label: 'נוצר על ידי' },
    { key: 'created_at', label: 'נוצר בתאריך' },
    { key: 'updated_by', label: 'עודכן על ידי' },
    { key: 'updated_at', label: 'עודכן בתאריך' },
  ];

  const applyFilter = () => {
    let newData = formsMetadata.map(toFormTableRow);
    Object.entries(filterValues).forEach(([col, selected]) => {
      if (selected.length > 0) {
        newData = newData.filter((row) => {
          const value = row[col as keyof FormTableRow];
          const cell = String(value ?? '');
          return selected.includes(cell);
        });
      }
    });
    setFilteredData(newData);
  };

  const clearFilter = (col: string) => {
    const newFilter = { ...filterValues };
    delete newFilter[col];
    setFilterValues(newFilter);
    setFilteredData(formsMetadata.map(toFormTableRow));
  };

  const updateFilter = (col: string, values: string[]) => {
    setFilterValues((prev) => ({ ...prev, [col]: values }));
  };

  const loadMergedFormTemplate = async (formId: string): Promise<FormTemplate> => {
    const declaredLangs = normalizeFormLanguageCodes(formLanguagesById[formId]);
    const loadResult = await loadSplitTemplateArtifacts(formId, declaredLangs, {
      getStructure: (id) => getFormStructure(id, translation, logout),
      getStrings: (id, lang) => getFormStrings(id, lang, translation, logout),
      getLegacyMono: (id) => getFormTemplate(id, translation, logout),
    });
    return mergedFormTemplateFromLoadResult(loadResult);
  };

  const handleEdit = (formId: string) => {
    navigate(`${EDIT_FORM_PATH}/${formId}`);
  };

  const handleDelete = async (formId: string) => {
    try {
      setLoadingDelete(true);
      await deleteFormTemplate(formId, translation, logout);
    } catch (error) {
      logClientError('handleDelete.deleteFormTemplate', error);
    } finally {
      setLoadingDelete(false);
      setFormIdToDelete(null);
      // Refresh the forms metadata after deletion
      const response = await getFormsMetadata(translation, logout);
      const refreshed = response.data as FormMetadata[];
      setFormLanguagesById(buildFormLanguagesById(refreshed));
      setFilteredData(refreshed.map(toFormTableRow));
      setFormIdToDelete(null);
    }
  };

  const handleViewTable = async (formId: string) => {
    try {
      const template = await loadMergedFormTemplate(formId);
      setSelectedFormTemplate(template);
      setShowTableModal(true);
    } catch (error) {
      logClientError('handleViewTable.loadFormTemplate', error);
    }
  };

  const openPreviewModal = (formId: string) => {
    const formLanguages = formLanguagesById[formId];
    setPreviewFormId(formId);
    setPreviewLanguage(defaultPreviewLanguage(formLanguages));
    setPreviewExpiryHours(null);
    setPreviewLinkUrl(null);
    setPreviewLinkExpiresAt(null);
    setPreviewLinkLanguage(null);
    setPreviewLinkError(null);
    setCopyFeedback(false);
  };

  const handleCreatePreviewLink = async () => {
    if (!previewFormId || previewExpiryHours == null) return;
    setPreviewLinkError(null);
    setPreviewLinkUrl(null);
    setPreviewLinkExpiresAt(null);
    setPreviewLinkLanguage(null);
    setPreviewLinkLoading(true);
    try {
      const result = await generatePreviewLink(
        previewFormId,
        translation,
        logout,
        previewExpiryHours,
        langCodeForLanguage(previewLanguage),
      );
      setPreviewLinkUrl(result.preview_url);
      setPreviewLinkExpiresAt(result.expires_at ?? null);
      setPreviewLinkLanguage(result.language ?? langCodeForLanguage(previewLanguage));
    } catch (error: unknown) {
      setPreviewLinkError(error instanceof Error ? error.message : translation.general_sever_error);
    } finally {
      setPreviewLinkLoading(false);
    }
  };

  const handleCreateAnotherPreviewLink = () => {
    setPreviewLinkUrl(null);
    setPreviewLinkExpiresAt(null);
    setPreviewLinkLanguage(null);
    setPreviewLinkError(null);
    setPreviewLanguage(
      defaultPreviewLanguage(previewFormId !== null ? formLanguagesById[previewFormId] : undefined),
    );
    setPreviewExpiryHours(null);
  };

  const handleCopyPreviewLink = () => {
    if (previewLinkUrl) {
      navigator.clipboard.writeText(previewLinkUrl).then(() => {
        setCopyFeedback(true);
        window.setTimeout(() => setCopyFeedback(false), 2000);
      });
    }
  };

  const closePreviewLinkModal = () => {
    setPreviewFormId(null);
    setPreviewLanguage(Languages.HEBREW);
    setPreviewExpiryHours(null);
    setPreviewLinkUrl(null);
    setPreviewLinkExpiresAt(null);
    setPreviewLinkLanguage(null);
    setPreviewLinkError(null);
    setCopyFeedback(false);
  };

  const formatExpiresAt = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString('he-IL', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return isoString;
    }
  };

  const getEmptyFormTemplate = (formId: string): FormTemplate => ({
    form_id: formId,
    form_name: '',
    show_form_after_fill: true,
    questions: [],
  });

  return (
    <div className='forms-table-container'>
      <div className='forms-table-header'>
        <button
          className='add-form-button'
          onClick={() => {
            const numericIds = formsMetadata
              .map((f) => String(f.form_id))
              .filter((id) => /^[1-9]\d*$/.test(id))
              .map(Number);
            const newFormId = String(numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1);

            setFormTemplate(getEmptyFormTemplate(newFormId));
            navigate(`${CREATE_FORM_PATH}/${newFormId}`);
          }}
        >
          הוסף טופס +
        </button>
      </div>
      <table className='forms-table'>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>
                <div className='forms-table-column'>
                  <ColumnFilter
                    columnKey={col.key}
                    data={formsMetadata.map(toFormTableRow)}
                    selected={filterValues[col.key] || []}
                    updateFilter={updateFilter}
                    applyFilter={applyFilter}
                    clearFilter={clearFilter}
                  />
                  <span>{col.label}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...filteredData]
            .sort((a, b) => {
              if (Number(a.form_id) < Number(b.form_id)) return -1;
              if (Number(a.form_id) > Number(b.form_id)) return 1;
              return 0;
            })
            .map((form, idx) => (
              <tr key={idx} className='form-row'>
                <td>{form.form_id}</td>
                <td>{form.form_name}</td>
                <td>{form.created_by}</td>
                <td>{form.created_at}</td>
                <td>{form.updated_by}</td>
                <td style={{ position: 'relative' }}>
                  <div className='floating-row-actions'>
                    <button
                      className='table-icon-button'
                      onClick={() => handleViewTable(form.form_id)}
                      title='תצוגת טבלה'
                    >
                      <FaTable />
                    </button>
                    <button
                      className='link-icon-button'
                      onClick={() => openPreviewModal(form.form_id)}
                      title='צור קישור שאלון דמה'
                      disabled={previewLinkLoading}
                    >
                      <FaLink />
                    </button>
                    <button
                      className='edit-icon-button'
                      onClick={() => handleEdit(form.form_id)}
                      title='ערוך טופס'
                    >
                      <FaEdit />
                    </button>
                    <button
                      className='delete-icon-button'
                      onClick={() => setFormIdToDelete(form.form_id)}
                      title='מחק טופס'
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                  {form.updated_at}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {formIdToDelete && (
        <DeleteConfirmationModal
          text='האם אתה בטוח שברצונך למחוק את הטופס?'
          onCancel={() => setFormIdToDelete(null)}
          onConfirm={() => {
            handleDelete(formIdToDelete);
          }}
          loading={loadingDelete}
        />
      )}

      {showTableModal && selectedFormTemplate && (
        <QuestionTableModal
          questions={selectedFormTemplate.questions}
          formName={selectedFormTemplate.form_name}
          onClose={() => {
            setShowTableModal(false);
            setSelectedFormTemplate(null);
          }}
        />
      )}

      {previewFormId !== null && (
        <div
          className='modal-overlay'
          onClick={closePreviewLinkModal}
          role='dialog'
          aria-modal='true'
          aria-labelledby='preview-link-modal-title'
        >
          <div className='modal-content preview-link-modal' onClick={(e) => e.stopPropagation()}>
            <h3 id='preview-link-modal-title'>קישור שאלון דמה</h3>
            {previewLinkLoading && <p>יוצר קישור...</p>}
            {previewLinkError && <p className='preview-link-error'>{previewLinkError}</p>}
            {!previewLinkUrl && !previewLinkLoading && (
              <div className='preview-link-create-step' dir='rtl'>
                <p className='preview-link-language-label'>שפת השאלון:</p>
                <ul className='preview-link-language-list' role='listbox' aria-label='שפת השאלון'>
                  {previewLanguageOptions.map(({ lang, label }) => (
                    <li key={lang}>
                      <button
                        type='button'
                        className={`preview-link-language-option ${previewLanguage === lang ? 'selected' : ''}`}
                        onClick={() => setPreviewLanguage(lang)}
                        aria-pressed={previewLanguage === lang}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
                <p className='preview-link-expiry-label'>תוקף הקישור:</p>
                <ul className='preview-link-expiry-list' role='listbox' aria-label='תוקף הקישור'>
                  {PREVIEW_EXPIRY_OPTIONS.map(({ label, hours }) => (
                    <li key={hours}>
                      <button
                        type='button'
                        className={`preview-link-expiry-option ${previewExpiryHours === hours ? 'selected' : ''}`}
                        onClick={() => setPreviewExpiryHours(hours)}
                        aria-pressed={previewExpiryHours === hours}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type='button'
                  className='preview-link-create-btn'
                  onClick={handleCreatePreviewLink}
                  disabled={previewExpiryHours == null}
                  aria-label='צור קישור'
                >
                  צור קישור
                </button>
              </div>
            )}
            {previewLinkUrl && (
              <>
                <div className='preview-link-input-wrap'>
                  <input
                    type='text'
                    readOnly
                    value={previewLinkUrl}
                    className='preview-link-input'
                    aria-label='קישור תצוגה מקדימה'
                  />
                </div>
                {previewLinkExpiresAt && (
                  <p className='preview-link-expiry-hint' dir='rtl'>
                    הקישור תקף עד: {formatExpiresAt(previewLinkExpiresAt)}
                  </p>
                )}
                <p className='preview-link-language-hint' dir='rtl'>
                  שפת השאלון:{' '}
                  {languageFromCode(previewLinkLanguage ?? langCodeForLanguage(previewLanguage))}
                </p>
                <div className='preview-link-actions'>
                  <button
                    type='button'
                    className='preview-link-copy-btn'
                    onClick={handleCopyPreviewLink}
                    aria-label='העתק קישור'
                  >
                    <FaCopy className='preview-link-copy-icon' aria-hidden='true' />
                    <span>{copyFeedback ? 'הועתק!' : 'העתק קישור'}</span>
                  </button>
                  <button
                    type='button'
                    className='preview-link-create-another-btn'
                    onClick={handleCreateAnotherPreviewLink}
                    aria-label='צור קישור נוסף'
                  >
                    צור קישור נוסף
                  </button>
                </div>
              </>
            )}
            <button
              type='button'
              className='preview-link-close-btn'
              onClick={closePreviewLinkModal}
              aria-label='סגור'
            >
              סגור
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllFormsMetadata;
